import React from "react";
import { Link, NavLink } from "react-router-dom";
import Loading from "../common/Loading";
import { ACCENT } from "./shared";
import StudioShell from "./StudioShell";

const RIGHTS_TABS = [
  { to: "/artist-studio/rights", label: "Strikes & Appeals" },
  { to: "/artist-studio/rights/notices", label: "Notices Received" },
  { to: "/artist-studio/rights/claims", label: "Claims Received" },
  { to: "/artist-studio/rights/terms", label: "Terms & Disclosures" },
  { to: "/artist-studio/rights/holders", label: "Rights-holders" },
  { to: "/artist-studio/deactivate", label: "Deactivate account" },
];

// Wraps a post-approval Rights & Legal page: studio chrome, a spinner while
// loading, and a friendly message when the account isn't approved (or is
// deactivated) instead of a broken form.
// Other studio sections reuse it by passing their own `active` nav key, `heading`, `tabs` and `maxWidth`.
export default function StudioGate({
  account,
  title,
  allowDeactivated = false,
  active = "rights",
  heading = "Rights & Legal",
  tabs = RIGHTS_TABS,
  maxWidth = "max-w-2xl",
  headingAction = null,
  children,
}) {
  const { loading, failed, isApproved, isDeactivated } = account;

  let body;
  if (loading) {
    body = (
      <div className="flex items-center justify-center py-24">
        <Loading />
      </div>
    );
  } else if (failed) {
    body = <p className="text-gray-600">We couldn't load your artist account. Please refresh and try again.</p>;
  } else if (!isApproved) {
    body = (
      <p className="text-gray-600">
        This page is available once your artist account is approved.{" "}
        <Link to="/artist-studio" className="underline font-semibold">Go to Artist Studio</Link>
      </p>
    );
  } else if (isDeactivated && !allowDeactivated) {
    body = (
      <p className="text-gray-600">
        Your artist account is deactivated, so this page is read-only.{" "}
        <Link to="/artist-studio/profile" className="underline font-semibold">Back to Artist Profile</Link>
      </p>
    );
  } else {
    body = children;
  }

  return (
    <StudioShell active={active}>
      <div className={`w-full ${maxWidth}`}>
        {(heading || headingAction) && (
          <div className="flex items-start justify-between gap-4 mb-4">
            <h1 className="text-[28px] leading-tight font-bold text-gray-900">{heading}</h1>
            {headingAction}
          </div>
        )}
        {tabs.length > 0 && <nav className="flex flex-wrap gap-x-6 gap-y-1 mb-5" aria-label={`${typeof heading === "string" ? heading : "Studio"} sections`}>
          {tabs.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end
              className={({ isActive }) => `text-sm font-semibold transition ${isActive ? "" : "text-gray-400 hover:text-gray-700"}`}
              style={({ isActive }) => (isActive ? { color: ACCENT } : undefined)}
            >
              {t.label}
            </NavLink>
          ))}
        </nav>}
        {title && <h2 className="text-xl font-bold text-gray-900 mb-4">{title}</h2>}
        {body}
      </div>
    </StudioShell>
  );
}
